import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";

const inputCls =
  "w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";

const DIETS = [
  { v: "omnivore", l: "Omnivore" },
  { v: "vegetarian", l: "Vegetarian" },
  { v: "vegan", l: "Vegan" },
  { v: "pescatarian", l: "Pescatarian" },
];

const toCsv = (arr) => (arr || []).join(", ");
const fromCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export const FoodPrefsEditor = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [diet, setDiet] = useState("omnivore");
  const [csv, setCsv] = useState({ food_likes: "", food_dislikes: "", allergies: "" });

  useEffect(() => {
    if (!open || loaded) return;
    api
      .get("/profile")
      .then((r) => {
        const p = r.data?.profile;
        if (p) {
          setDiet(p.dietary_preference || "omnivore");
          setCsv({
            food_likes: toCsv(p.food_likes),
            food_dislikes: toCsv(p.food_dislikes),
            allergies: toCsv(p.allergies),
          });
        }
        setLoaded(true);
      })
      .catch(() => {});
  }, [open, loaded]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/diet", {
        dietary_preference: diet,
        food_likes: fromCsv(csv.food_likes),
        food_dislikes: fromCsv(csv.food_dislikes),
        allergies: fromCsv(csv.allergies),
      });
      toast.success("Preferences saved — generate a new plan to apply them");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="obs-card" data-testid="food-prefs-card">
      <button
        type="button"
        data-testid="food-prefs-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <SlidersHorizontal className="h-5 w-5 text-blaze" />
          <div>
            <div className="overline">Food preferences</div>
            <div className="text-xs text-zinc-400 mt-0.5">Likes, dislikes & allergies shape every AI plan</div>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
      </button>

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <div className="overline mb-1.5">Dietary preference</div>
            <select data-testid="food-prefs-diet" value={diet} onChange={(e) => setDiet(e.target.value)} className={inputCls}>
              {DIETS.map((d) => (
                <option key={d.v} value={d.v}>{d.l}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="overline mb-1.5">Allergies (comma separated)</div>
            <input
              data-testid="food-prefs-allergies"
              value={csv.allergies}
              onChange={(e) => setCsv((c) => ({ ...c, allergies: e.target.value }))}
              className={inputCls}
              placeholder="peanuts, shellfish"
            />
          </div>
          <div>
            <div className="overline mb-1.5">Food likes</div>
            <input
              data-testid="food-prefs-likes"
              value={csv.food_likes}
              onChange={(e) => setCsv((c) => ({ ...c, food_likes: e.target.value }))}
              className={inputCls}
              placeholder="chicken, oats, berries"
            />
          </div>
          <div>
            <div className="overline mb-1.5">Food dislikes</div>
            <input
              data-testid="food-prefs-dislikes"
              value={csv.food_dislikes}
              onChange={(e) => setCsv((c) => ({ ...c, food_dislikes: e.target.value }))}
              className={inputCls}
              placeholder="tofu, mushrooms"
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="button"
              data-testid="food-prefs-save"
              onClick={save}
              disabled={saving || !loaded}
              className="blaze-btn px-5 py-2.5 text-xs disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save preferences"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodPrefsEditor;
